import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, ArrowLeft } from "lucide-react";
import { mediaOptions } from "../../data/choiceData";
import { useNavigate } from "react-router-dom";
import Images from "../../utils/Images";
import Footer from "../../components/landing/Footer";
import OptionCard from "../../components/media_and_events/OptionCard";
import HeroSlider from "../../components/media_and_events/HeroSlider";


export default function MediaEvents() {
  const navigate = useNavigate();
  const [helperOpen, setHelperOpen] = useState(false);
  const [toast, setToast] = useState("");

  const heroImages = [
    Images.hero_img_web_three,
    Images.hero_img_web_four,
  ];

  // Transform mediaOptions to separate Conference and Trade Mission
  const transformedOptions = mediaOptions.flatMap((opt) => {
    if (opt.key === "conference-trade-mission") {
      // Split into two separate cards
      return [
        {
          key: "conference",
          title: "Conference",
          subtitle: "Connect, learn, and engage",
          image: opt.image,
          link: "/conference-trade-mission",
        },
        {
          key: "trade-mission",
          title: "Trade Mission",
          subtitle: "Explore markets, build partnerships",
          image: Images.trade_mission,
          link: "/conference-trade-mission",
        },
      ];
    }
    return [opt];
  });

  const handleCardClick = (opt) => {
    if (opt.comingSoon) {
      setToast("This item is coming soon");
      setTimeout(() => setToast(""), 1800);
      return;
    }

    if (opt.link) {
      navigate(opt.link);
    } else {
      setToast("Feature coming soon");
      setTimeout(() => setToast(""), 1800);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section with Image Slider */}
      <HeroSlider images={heroImages} showBackButton={false} />

      {/* Choice Section */}
      <section className="px-4 py-12 bg-gradient-to-b from-gray-50 to-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex gap-4 justify-between items-center mb-8">
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                Media and Events
              </h2>
              <div className="mt-2 w-32 h-1 bg-emerald-500 rounded" />
            </div>
            <motion.button
              onClick={() => navigate("/")}
              whileHover={{ scale: 1.03, y: -1 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200 shadow-sm transition-colors duration-200 hover:bg-emerald-100 hover:border-emerald-300"
            >
              <ArrowLeft size={16} />
              Back to Main Menu
            </motion.button>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35 }}
              className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4"
            >
              {transformedOptions.map((opt, index) => (
                <motion.div
                  key={`${opt.key}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="w-full"
                >
                  <OptionCard
                    option={opt}
                    onClick={() => handleCardClick(opt)}
                  />
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Helper bubble */}
          <motion.button
            onClick={() => setHelperOpen((v) => !v)}
            className="inline-flex fixed bottom-4 left-4 z-30 justify-center items-center w-12 h-12 text-white bg-gray-900 rounded-full shadow-lg sm:left-6 sm:bottom-6"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <HelpCircle size={20} />
          </motion.button>

          <AnimatePresence>
            {helperOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.25 }}
                className="fixed left-4 sm:left-6 bottom-16 sm:bottom-20 z-30 p-3 sm:p-4 w-80 sm:w-72 max-w-[calc(100vw-2rem)] sm:max-w-none text-xs sm:text-sm text-gray-700 bg-white rounded-xl border border-gray-200 shadow-xl"
              >
                <p className="mb-2 font-semibold text-gray-900">How it works</p>
                <ul className="pl-4 space-y-1 list-disc sm:pl-5">
                  <li>Choose an option to explore available services.</li>
                  <li>Items marked as Coming soon are not yet available.</li>
                  <li>Browse our media and event offerings.</li>
                </ul>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Toast */}
          <AnimatePresence>
            {toast && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                className="fixed bottom-6 left-1/2 z-30 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-lg -translate-x-1/2"
              >
                {toast}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}

